import { CircularProgress, Container, Grid, SvgIcon, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { composeWhereClause, displayChildAge, displayFacilityType } from '../../modules/data';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber } from '../../modules/text';
import MessageBox from '../MessageBox';

function AccessInfographic() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [children, setChildren] = useState(null);
  const { settingType, childAge } = useContext(FiltersContext);
  const { primaryColor, secondaryColor } = useContext(StoryContext);

  useEffect(() => {
    // Instantiate a new server to define where the RDS API is hosted.
    // const server = new RdsServer(process.env.REACT_APP_RDS_SERVER);
    function getCapacity() {
      // Instantiate a catalog that exists on the server
      // const catalog = new RdsCatalog(server, 'ccdc');
      // Instantiate a data product that exists on the catalog
      // const dataProduct = new RdsDataProduct(catalog, 'county_licensed_capacity');

      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND capacity_type=LIC`;

      whereClause += composeWhereClause({ settingType, childAge });

      return rdsTabulate('ccdc', 'county_licensed_capacity', {
        dims: 'state_fips',
        measure: 'capacity:SUM(capacity)',
        where: whereClause,
      });
    }

    function getChildrenCount() {
      // Instantiate a catalog that exists on the server
      // const catalog = new RdsCatalog(server, '3rdparty');
      // Instantiate a data product that exists on the catalog
      // const dataProduct = new RdsDataProduct(catalog, 'acs_sams_state_wide');

      const whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;

      return rdsTabulate('3rdparty', 'acs_sams_state_wide', {
        dims: 'state_fips',
        measure: 'children:SUM(cnt_parents_inlf_total)',
        where: whereClause,
      });
    }

    setReady(false);
    setError(null);

    Promise.all([getCapacity(), getChildrenCount()])
      .then((responses) => {
        // console.log(responses);
        if (responses[0].parsedBody.records.length > 0) {
          setCapacity(responses[0].parsedBody.records[0][1]);
        } else {
          setCapacity(null);
        }

        if (responses[1].parsedBody.records.length > 0) {
          setChildren(responses[1].parsedBody.records[0][1]);
        } else {
          setChildren(null);
        }
      })
      .catch((rdsError) => {
        // console.error(rdsError);
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [settingType, childAge, stateCode]);

  if (!ready) {
    return (<CircularProgress />);
  }

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={10} sm={6}>
        {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
        <MessageBox>
          {capacity && children
            && (
              <Typography variant="body1" align="left">
                <b>{states[stateCode.toUpperCase()]}</b> has {toFormattedNumber((capacity / children) * 100)} <b>{displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b> slots for&nbsp;
                <b>{displayChildAge(childAge, true).toLowerCase()}</b> for every 100 children whose parents work.
              </Typography>
            )}

          {(!capacity || !children)
            && (
              <Typography variant="body1" align="left">
                Not reported
              </Typography>
            )}
        </MessageBox>
      </Grid>
      <Grid item xs={12}>
        <Container style={{ textAlign: 'center' }}>
          <SvgIcon style={{ fontSize: '20em' }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="32.667 168.5 537.663 458.673">
              <defs>
                <linearGradient id="accessGradient" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={primaryColor} />
                  <stop offset={`${(capacity / children) * 100}%`} stopColor={primaryColor} />
                  <stop offset={`${(capacity / children) * 100 + 0.1}%`} stopColor={secondaryColor} />
                  <stop offset="100%" stopColor={secondaryColor} />
                </linearGradient>
              </defs>
              <g fill="url('#accessGradient')">
                <path d="M140.391,318.706c-6.29,4.506-18.868,6.65-23.583,7.022c-18.705,1.969-45.35-8.488-64.316-23.244
	c-9.002-7.011-18.77-13.738-19.688-25.158l0,0c-0.93-11.375,2.778-20.673,14.351-21.745c3.281-0.306,12.218,3.85,15.313,4.233
	c24.305,2.964,50.654-9.757,54.953-2.188c5.25,9.166,4.682,12.634,20.848,26.525c2.942,3.555,9.199,6.06,10.555,11.015
	C151.625,305.35,150.575,310.086,140.391,318.706z"/>
                <circle cx="442.809" cy="540.356" r="70.387" />
                <path d="M446.878,453.66c-47.883-2.248-88.521,34.745-90.771,82.628c-2.248,47.882,34.745,88.521,82.628,90.77
	c47.882,2.249,88.521-34.745,90.77-82.627c0.128-2.717,0.128-5.438,0-8.154C527.363,491.562,491.593,455.796,446.878,453.66z
	 M447.764,616.048c-41.804,2.756-77.926-28.898-80.682-70.701c-2.756-41.804,28.898-77.926,70.702-80.682
	s77.926,28.898,80.682,70.701c0.218,3.312,0.219,6.634,0.002,9.945C515.954,583.275,485.728,613.517,447.764,616.048
	L447.764,616.048z"/>
                <circle cx="175.338" cy="527.624" r="83.119" />
                <path d="M180.02,428.207c-54.912-2.591-101.527,39.823-104.118,94.735c-2.591,54.911,39.823,101.526,94.734,104.118
	c54.912,2.591,101.527-39.823,104.119-94.735c0.147-3.122,0.147-6.249,0-9.372C272.306,471.68,231.293,430.663,180.02,428.207z
	 M179.473,615.566c-48.57,2.284-89.796-35.237-92.08-83.808s35.238-89.796,83.808-92.08c48.57-2.284,89.796,35.238,92.08,83.809
	c0.129,2.756,0.129,5.516,0,8.271C261.102,577.111,224.826,613.388,179.473,615.566L179.473,615.566z"/>
                <path d="M562.844,322.523c-2.467-8.605-6.036-16.856-10.621-24.545c-9.002-15.313-31.063-11.157-36.555,5.731
	c-0.473,1.477-1.077,2.908-1.805,4.277c-10.644,19.688-35.353,19.886-67.816,21.876c-56.135,3.5-210.505,25.158-227.579,1.707
	c-6.727-9.253,2.439-17.031,9.264-20.575c2.53-1.294,5.155-2.391,7.854-3.281c0.139,0.018,0.279-0.026,0.383-0.121h0.131
	c26.536-12.283,44.321-35.625,44.343-62.271c0-39.596-39.191-71.744-87.724-71.787c-48.533-0.044-87.801,31.95-87.844,71.568l0,0
	c0.04,1.721,1.467,3.084,3.188,3.044c0.031-0.001,0.062-0.002,0.093-0.004c7.361-0.339,13.902,1.192,17.829,8.04
	c1.094,1.881,1.903,3.511,2.691,5.042c2.275,4.485,3.653,7.197,13.388,15.554l3.205,2.888c3.281,2.483,8.171,6.235,10.03,12.973
	c3.894,14.307,0.897,23.058-11.431,33.471l-0.754,0.602c-3.223,2.196-6.75,3.91-10.468,5.086c-5.425,1.76-11.025,2.922-16.703,3.467
	l0,0c-3.406,0.35-6.543,2.009-8.75,4.626L79.148,373.21c-4.559,6.148-7.985,13.06-10.118,20.411
	c-2.811,10.118-4.228,20.573-4.211,31.075c0,17.687,7.591,35.942,19.218,52.306c18.065-32.591,52.109-53.105,89.365-53.848
	c56.627-1.094,104.208,43.916,106.308,100.511c0.293,7.694-0.268,15.398-1.673,22.97c24.219,0.997,48.477,0.533,72.64-1.39
	c-0.076-1.629-0.131-3.281-0.131-4.933c-0.023-50.932,41.246-92.238,92.177-92.262c31.315-0.014,60.498,15.864,77.495,42.165
	c21.439-20.017,39.443-46.05,46.641-75.966C572.306,391.586,571.616,353.609,562.844,322.523z M158.286,252.289
	c-6.604,2.988-14.381,0.056-17.369-6.549c-2.988-6.605-0.056-14.381,6.549-17.369c6.605-2.988,14.381-0.056,17.369,6.549
	c0.425,0.94,0.738,1.928,0.933,2.941C166.909,243.822,163.815,249.788,158.286,252.289z"/>              </g>
            </svg>
          </SvgIcon>
        </Container>
      </Grid>
    </Grid>
  );
}

export default AccessInfographic;
